import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useTenant } from '@core/tenant';
import { COMPLETE_PROFILE_PATH, isProfileIncomplete } from './profileCompletion';
import { canBypassGuarantee } from '@features/booking/utils/agentUtils';

const EXCLUDED_PATH_PREFIXES = [
  '/auth-return',
  '/auth-return-complete',
  '/auth/',
  COMPLETE_PROFILE_PATH,
];

function isExcludedPath(pathname: string): boolean {
  if (pathname === COMPLETE_PROFILE_PATH) return true;
  return EXCLUDED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Renderless enforcer that mirrors the legacy invariant:
 * an authenticated user must have name + phone before doing anything else.
 *
 * Whenever the route changes (or auth state updates) it redirects to
 * /complete-profile if the profile is missing data, the user is not an
 * agent of the current restaurant, and the current path is not part of
 * the auth callback chain.
 */
export function ProfileCompletionEnforcer() {
  const { authState, isLoading } = useAuth();
  const { restaurant } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();

  const { isAuthenticated, name, phone, roles, userRestaurantId } = authState;
  const restaurantId = restaurant?.id;

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (!isProfileIncomplete({ name, phone })) return;
    if (isExcludedPath(location.pathname)) return;

    // Agents reserve on behalf of guests; their own profile completeness
    // is irrelevant to the booking. Skip the redirect when the logged-in
    // user is an agent of the current restaurant. If tenant is not ready
    // we cannot evaluate agent status — be conservative and wait.
    if (restaurantId == null) return;
    if (canBypassGuarantee(roles, userRestaurantId, restaurantId)) {
      return;
    }

    navigate(COMPLETE_PROFILE_PATH, {
      replace: true,
      state: { from: location.pathname + location.search },
    });
  }, [
    isLoading,
    isAuthenticated,
    name,
    phone,
    roles,
    userRestaurantId,
    location.pathname,
    location.search,
    restaurantId,
    navigate,
  ]);

  return null;
}

export default ProfileCompletionEnforcer;
